import React, { useState } from 'react';
import { Card as CardType } from '../services/api';
import { EditPencil, Link, Check, Lock } from 'iconoir-react';
import { CardModal } from './CardModal';
import { CardOverageLockedModal } from './CardOverageLockedModal';

interface CardProps {
  card: CardType;
  onUpdate: (card: CardType) => void;
  onDelete: (cardId: number) => void;
  onArchive: (cardId: number, archived: boolean) => void;
  onDragStart: (card: CardType) => void;
  onDragEnd: () => void;
  initiallyOpen?: boolean;
  userRole?: string;
  boardId?: number;
  onRefresh?: () => void;
}

export const Card: React.FC<CardProps> = ({ card, onUpdate, onDelete, onArchive, onDragStart, onDragEnd, initiallyOpen = false, userRole = 'visitor', boardId, onRefresh }) => {
  const canEdit = userRole === 'admin' || userRole === 'editor';
  const [isModalOpen, setIsModalOpen] = useState(!card.locked && initiallyOpen);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(card.title);
  const [isHovered, setIsHovered] = useState(false);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  
  // Debug: vérifier si les users sont chargés
  console.log('Card users:', card.users);

  // Ouvrir la modale si initiallyOpen change (ex: lien direct ?card=id) — jamais
  // pour une carte verrouillée.
  React.useEffect(() => {
    if (initiallyOpen) {
      if (card.locked) {
        setIsLockedModalOpen(true);
      } else {
        setIsModalOpen(true);
      }
    }
  }, [initiallyOpen, card.locked]);

  const handleSaveTitle = () => {
    if (editTitle.trim()) {
      const truncatedTitle = editTitle.trim().substring(0, 144);
      onUpdate({ ...card, title: truncatedTitle });
      setIsEditingTitle(false);
    }
  };

  const handleCancelTitle = () => {
    setEditTitle(card.title);
    setIsEditingTitle(false);
  };

  const handleCardClick = () => {
    if (isEditingTitle) return;
    // Carte verrouillée : ouvre la modale de dépassement plutôt que CardModal.
    if (card.locked) {
      setIsLockedModalOpen(true);
      return;
    }
    setIsModalOpen(true);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingTitle(true);
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Construire l'URL avec un paramètre pour ouvrir la modale
    const url = `${window.location.origin}${window.location.pathname}?card=${card.id}`;
    
    try {
      await navigator.clipboard.writeText(url);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie du lien', err);
    }
  };

  const handleDragStart = (e: React.DragEvent) => {
    // Une carte verrouillée ne peut pas être déplacée (cf. CardController::move).
    if (!canEdit || card.locked) {
      e.preventDefault();
      return;
    }

    onDragStart(card);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('card', JSON.stringify(card));
  };

  if (isEditingTitle) {
    return (
      <div className="card-form">
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={handleSaveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSaveTitle();
            if (e.key === 'Escape') handleCancelTitle();
          }}
          placeholder="Titre de la carte..."
          maxLength={144}
          autoFocus
          className="card-title-input"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className={`kanban-card${card.locked ? ' kanban-card-locked' : ''}`}
        draggable={canEdit && !card.locked}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          cursor: 'pointer',
          borderLeft: card.label_color ? `10px solid ${card.label_color}` : 'none',
          opacity: card.locked ? 0.6 : 1
        }}
      >
        {card.labels && card.labels.length > 0 && (
          <div className="card-label-strips">
            {card.labels.map(label => (
              <div
                key={label.id}
                className="card-label-strip"
                style={{ backgroundColor: label.color }}
                title={label.name}
              />
            ))}
          </div>
        )}
        <div className="card-header">
          <div className="card-title-container">
            {card.locked && (
              <span title="Carte verrouillée : dépassement de votre offre Premium">
                <Lock
                  width={14}
                  height={14}
                  strokeWidth={2}
                  style={{ marginRight: 6, verticalAlign: 'middle' }}
                />
              </span>
            )}
            {card.title}
          </div>
          <div className="card-actions">
            {isHovered && (
              <>
                {canEdit && !card.locked && (
                  <button
                    className="btn-edit-icon"
                    onClick={handleEditClick}
                    title="Modifier le titre"
                  >
                    <EditPencil width={16} height={16} strokeWidth={2} />
                  </button>
                )}
                <button
                  className="btn-link-icon"
                  onClick={handleCopyLink}
                  title="Copier le lien"
                >
                  {showCopiedFeedback ? (
                    <Check width={16} height={16} strokeWidth={2} />
                  ) : (
                    <Link width={16} height={16} strokeWidth={2} />
                  )}
                </button>
              </>
            )}
          </div>
        </div>
        {card.description && (
          <div className="card-description-preview">
            {card.description}
          </div>
        )}
        {card.users && card.users.length > 0 && (
          <div className="card-assigned-users">
            {card.users.map(user => (
              <div
                key={user.id}
                className="card-user-avatar"
                title={user.name}
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="card-user-avatar-image" />
                ) : (
                  user.name.charAt(0).toUpperCase()
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <CardOverageLockedModal
        isOpen={isLockedModalOpen}
        onClose={() => setIsLockedModalOpen(false)}
        card={card}
        userRole={userRole}
        onDelete={onDelete}
      />

      <CardModal
        card={card}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          // Nettoyer l'URL si elle contient le paramètre card
          const url = new URL(window.location.href);
          if (url.searchParams.has('card')) {
            url.searchParams.delete('card');
            window.history.replaceState({}, '', url.toString());
          }
        }}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onArchive={onArchive}
        userRole={userRole}
        boardId={boardId}
        onRefresh={onRefresh}
      />
    </>
  );
};
